import React, { useEffect, useState } from 'react'
import {
  isExpandableRowItem,
  isAsyncExpandableRowItem,
  RowItemElement,
  RowItemExpandableElement,
  AsyncRowItemExpandableElement,
} from './RowItemProp'
import { AccordionGridRow } from './AccordionGridRow'

interface AccordionProps {
  gridTemplateColumns?: string
  items: RowItemExpandableElement[] | RowItemElement[]
  nothingToShow: JSX.Element
  noMaxHeight?: boolean
  defaultExpanded?: boolean
  minExpandableCount?: number
}

/**
 * AccordionGridBody
 *
 * This components is used for displaying expandable row grids, when the grid
 * and the widget to show when a row is expanded
 *
 * @param props
 * @constructor
 */
export const AccordionGridBody = (props: AccordionProps): JSX.Element => {
  const [accordionState, setAccordionState] = useState<number[]>(
    props.items.map((item) => {
      if (props.defaultExpanded && isExpandableRowItem(item)) {
        return item.total
      }
      return 0
    }),
  )

  const itemsLength = props.items.length
  useEffect(() => {
    setAccordionState((prev) => {
      if (prev.length === itemsLength) {
        return prev
      }

      const next = [...prev]
      if (itemsLength > prev.length) {
        for (let i = prev.length; i < itemsLength; i++) {
          const item = props.items[i]
          if (props.defaultExpanded && isExpandableRowItem(item)) {
            next.push(item.total)
          } else {
            next.push(0)
          }
        }
      } else {
        next.length = itemsLength
      }
      return next
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsLength, props.defaultExpanded])

  const itemTotals = props.items
    .map((item) => (isExpandableRowItem(item) ? item.total : 0))
    .join(',')
  useEffect(() => {
    setAccordionState((prev) =>
      props.items.map((item, idx) => {
        const total = isExpandableRowItem(item) ? item.total : 0
        if (
          prev[idx] > 0 ||
          (props.defaultExpanded && isExpandableRowItem(item))
        ) {
          return total
        }
        return 0
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemTotals])

  if (props.items.length === 0) {
    return props.nothingToShow
  }
  const initalGridOffset = 2
  const onExpanded = (idx: number, size: number) => {
    setAccordionState((prev) => {
      const next = [...prev]
      next[idx] = size
      return next
    })
  }
  let gridRow = 0

  return (
    <>
      {props.items.map((item, idx) => {
        const offSet = 1 + accordionState[idx - 1] || initalGridOffset
        gridRow += idx === 0 ? initalGridOffset : offSet
        const minCount = props.minExpandableCount ?? 1
        const isExpandable = isExpandableRowItem(item) && item.total > minCount
        const total = isExpandableRowItem(item) ? item.total : 0

        return (
          <AccordionGridRow
            {...item}
            gridTemplateColumns={props.gridTemplateColumns}
            key={item.key}
            isExpanded={isExpandable && accordionState[idx] === total}
            expandedElement={isExpandable ? item.expandedItems : undefined}
            gridRow={gridRow}
            onExpanded={(state) => onExpanded(idx, state ? total : 0)}
            isDisabled={item.isDisabled}
            noMaxHeight={props.noMaxHeight}
          />
        )
      })}
    </>
  )
}

interface AsyncProps {
  gridTemplateColumns?: string
  items: AsyncRowItemExpandableElement[] | RowItemElement[]
  nothingToShow: JSX.Element
}
/**
 * AsyncAccordionGridBody
 *
 * @deprecated use AccordionGridBody instead
 *
 * This components is used for displaying expandable row grids, when the grid
 * and the widget to show when a row is expanded
 *
 * @param props
 * @constructor
 */
export function AsyncAccordionGridBody(props: AsyncProps): JSX.Element {
  const [accordionState, setAccordionState] = useState<number[]>(
    props.items.map(() => 1),
  )

  if (props.items.length === 0) {
    return props.nothingToShow
  }
  const initalGridOffset = 2
  const onExpanded = (idx: number, size: number) => {
    accordionState[idx] = size
    setAccordionState(accordionState)
  }
  let gridRow = 0

  return (
    <>
      {props.items.map((item, idx) => {
        const offSet = 1 + accordionState[idx - 1] || initalGridOffset
        gridRow += idx === 0 ? initalGridOffset : offSet
        const isExpandable = isAsyncExpandableRowItem(item) && item.total > 1
        const total = isAsyncExpandableRowItem(item) ? item.total : 0
        return (
          <AccordionGridRow
            {...item}
            gridTemplateColumns={props.gridTemplateColumns}
            key={item.key}
            isExpanded={true}
            expandedElement={isExpandable ? item.asyncCall : undefined}
            gridRow={gridRow}
            onExpanded={(state) => onExpanded(idx, state ? total : 0)}
          />
        )
      })}
    </>
  )
}
